import { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Wrench, AlertCircle, ClipboardCheck, Eye, ChevronDown, Droplet, ThermometerSun, Image as ImageIcon, Paperclip, Pencil, Trash2, Save } from "lucide-react";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";
import ApplianceAssessmentDialog from "@/components/ApplianceAssessmentDialog";
import PlumbingAssessmentDialog from "@/components/PlumbingAssessmentDialog";
import HvacAssessmentDialog from "@/components/HvacAssessmentDialog";
import ExportMenu from "@/components/ExportMenu";
import AssessmentExportMenu from "@/components/AssessmentExportMenu";
import EmptyState from "@/components/EmptyState";
import AiSummarizeButton from "@/components/AiSummarizeButton";

const PRIORITY_COLORS = {
  low: "bg-slate-100 text-slate-700",
  medium: "bg-blue-100 text-blue-700",
  high: "bg-amber-100 text-amber-700",
  urgent: "bg-rose-100 text-rose-700",
};

const STATUS_COLORS = {
  open: "bg-amber-100 text-amber-700",
  in_progress: "bg-blue-100 text-blue-700",
  resolved: "bg-emerald-100 text-emerald-700",
  closed: "bg-slate-100 text-slate-700",
};

export default function MaintenancePage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [properties, setProperties] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ property_id: "", title: "", description: "", priority: "medium" });
  const [assessOpen, setAssessOpen] = useState(false);
  const [plumbingOpen, setPlumbingOpen] = useState(false);
  const [hvacOpen, setHvacOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [filter, setFilter] = useState("all"); // "all" | "assessments"

  const load = async () => {
    const [m, p] = await Promise.all([api.get("/maintenance"), api.get("/properties")]);
    setItems(m.data); setProperties(p.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/maintenance", form);
      toast.success("Request submitted");
      setOpen(false); setForm({ property_id: "", title: "", description: "", priority: "medium" }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const updateStatus = async (id, status) => {
    await api.patch(`/maintenance/${id}`, { status });
    toast.success("Updated"); load();
  };

  const isAdmin = user?.role === "super_admin" || user?.role === "landlord";

  const openEdit = (m) => {
    setEditing(m);
    setEditForm({
      title: m.title || "",
      description: m.description || "",
      landlord_notes: m.landlord_notes || "",
      assessment: { ...(m.assessment || {}) },
    });
    setViewing(null);
  };

  const saveEdit = async () => {
    try {
      await api.put(`/maintenance/${editing.id}/assessment`, editForm);
      toast.success("Maintenance record updated");
      setEditing(null); setEditForm(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    }
  };

  const removeMaintenance = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/maintenance/${confirmDelete.id}`);
      toast.success("Maintenance record deleted");
      setConfirmDelete(null); setViewing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  const visibleItems = filter === "assessments" ? items.filter((m) => !!m.assessment_type) : items;
  const assessmentCount = items.filter((m) => !!m.assessment_type).length;

  return (
    <AppLayout
      title="Maintenance Requests"
      subtitle={user?.role === "landlord" ? "All incoming requests from your tenants" : "Submit and track your requests"}
      actions={
        <div className="flex gap-2">
          {user?.role === "landlord" && (
            <ExportMenu
              endpoint="/maintenance/export.csv"
              filenameBase="rojasproperty-maintenance"
              testidPrefix="export-maintenance"
              disabled={items.length === 0}
            />
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button data-testid="open-assessment-menu" variant="outline" className="gap-2">
                <ClipboardCheck className="h-4 w-4" /> Assessments <ChevronDown className="h-3.5 w-3.5 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuItem data-testid="assess-appliance" onClick={() => setAssessOpen(true)} className="gap-2 cursor-pointer">
                <ClipboardCheck className="h-4 w-4 text-slate-700" />
                <div><div className="font-medium">Appliance — Broken/Damaged</div><div className="text-xs text-slate-500">Fridge, dryer, oven, etc.</div></div>
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="assess-plumbing" onClick={() => setPlumbingOpen(true)} className="gap-2 cursor-pointer">
                <Droplet className="h-4 w-4 text-blue-600" />
                <div><div className="font-medium">Plumbing / Water Damage</div><div className="text-xs text-slate-500">Leaks, flooding, mold</div></div>
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="assess-hvac" onClick={() => setHvacOpen(true)} className="gap-2 cursor-pointer">
                <ThermometerSun className="h-4 w-4 text-orange-600" />
                <div><div className="font-medium">HVAC Failure</div><div className="text-xs text-slate-500">Heating / cooling / thermostat</div></div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-maintenance-button" className="bg-slate-900 hover:bg-slate-800 gap-2"><Plus className="h-4 w-4" /> New Request</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Submit maintenance request</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3 pt-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Property</Label>
                <Select value={form.property_id} onValueChange={v => setForm({ ...form, property_id: v })}>
                  <SelectTrigger data-testid="maint-property-select"><SelectValue placeholder="Select property" /></SelectTrigger>
                  <SelectContent>{properties.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label className="text-xs">Title</Label><Input data-testid="maint-title" required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Leaking kitchen sink" /></div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Description</Label>
                  {isAdmin && (
                    <AiSummarizeButton
                      kind="maintenance"
                      title={form.title}
                      text={form.description}
                      onApplySeverity={(sev) => setForm((f) => ({ ...f, priority: sev }))}
                    />
                  )}
                </div>
                <Textarea data-testid="maint-description" required rows={4} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Priority</Label>
                <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
                  <SelectTrigger data-testid="maint-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter><Button data-testid="maint-submit" type="submit" className="bg-slate-900 hover:bg-slate-800">Submit</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      }
    >
      <ApplianceAssessmentDialog
        open={assessOpen}
        onOpenChange={setAssessOpen}
        properties={properties}
        onCreated={load}
      />
      <PlumbingAssessmentDialog
        open={plumbingOpen}
        onOpenChange={setPlumbingOpen}
        properties={properties}
        onCreated={load}
      />
      <HvacAssessmentDialog
        open={hvacOpen}
        onOpenChange={setHvacOpen}
        properties={properties}
        onCreated={load}
      />

      <Dialog open={!!viewing} onOpenChange={(v) => !v && setViewing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="assessment-view-dialog">
          {viewing && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between gap-3 print:hidden">
                  <DialogTitle className="flex items-center gap-2">
                    {viewing.assessment_type === "plumbing" ? <Droplet className="h-5 w-5 text-blue-600" /> :
                     viewing.assessment_type === "hvac" ? <ThermometerSun className="h-5 w-5 text-orange-600" /> :
                     <ClipboardCheck className="h-5 w-5" />}
                    {viewing.assessment_type === "plumbing" ? "Plumbing / Water Damage Assessment" :
                     viewing.assessment_type === "hvac" ? "HVAC Failure Assessment" :
                     "Appliance Assessment"}
                  </DialogTitle>
                  {user?.role !== "tenant" && (
                    <div className="flex items-center gap-1.5">
                      <AssessmentExportMenu
                        maintenanceId={viewing.id}
                        filenameBase={`rojasproperty-${viewing.assessment_type || "assessment"}`}
                      />
                      {isAdmin && (
                        <>
                          <Button data-testid={`view-edit-${viewing.id}`} variant="outline" size="sm" onClick={() => openEdit(viewing)} className="gap-1.5 rounded-none border-bone hover:border-gold hover:text-gold">
                            <Pencil className="h-3.5 w-3.5" /> Edit
                          </Button>
                          <Button data-testid={`view-delete-${viewing.id}`} variant="outline" size="sm" onClick={() => setConfirmDelete(viewing)} className="gap-1.5 rounded-none border-bone hover:border-rose-400 hover:text-rose-600">
                            <Trash2 className="h-3.5 w-3.5" /> Delete
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </DialogHeader>
              <div className="space-y-3 text-sm pt-2">
                <ViewRow label="Property" value={viewing.property_name} />

                {viewing.assessment_type === "appliance" && (<>
                  <ViewRow label="1. Broken appliances" value={viewing.assessment?.any_broken_currently} />
                  <ViewRow label="2. Severity" value={viewing.assessment?.severity} />
                  <ViewRow label="3. Noticed at" value={viewing.assessment?.noticed_at} />
                  <ViewRow label="4. Currently broken on property" value={viewing.assessment?.property_has_broken} />
                  {viewing.assessment?.property_has_broken === "Yes" && (<>
                    <ViewRow label="4.0 Require repair" value={viewing.assessment?.total_require_repair} />
                    <ViewRow label="4.1 Require replacement" value={viewing.assessment?.total_require_replacement} />
                    {Number(viewing.assessment?.total_require_replacement) > 1 && (
                      <ViewRow label="4.1.0 Tagged & removed" value={viewing.assessment?.tagged_and_removed} />
                    )}
                    <ViewRow label="4.2 Description" value={viewing.assessment?.description_of_damage} multiline />
                    <ViewRow label="4.3 Contacted insurance" value={viewing.assessment?.contacted_insurance} />
                    {viewing.assessment?.contacted_insurance === "Yes" &&
                      <ViewRow label="4.3.0 Insurance details" value={viewing.assessment?.insurance_details} multiline />}
                    {viewing.assessment?.contacted_insurance === "No" &&
                      <ViewRow label="4.4 Insurance follow-up" value={viewing.assessment?.insurance_followup} multiline />}
                  </>)}
                </>)}

                {viewing.assessment_type === "plumbing" && (<>
                  <ViewRow label="1. Active leak" value={viewing.assessment?.active_leak} />
                  <ViewRow label="2. Severity" value={viewing.assessment?.severity} />
                  <ViewRow label="3. Noticed at" value={viewing.assessment?.noticed_at} />
                  <ViewRow label="4. Location" value={viewing.assessment?.location} multiline />
                  <ViewRow label="5. Water shut-off used" value={viewing.assessment?.shutoff_used} />
                  {viewing.assessment?.shutoff_used === "No" &&
                    <ViewRow label="5.0 Reason not used" value={viewing.assessment?.shutoff_reason_not_used} multiline />}
                  <ViewRow label="6. Mold/damage visible" value={viewing.assessment?.mold_or_damage_visible} />
                  {viewing.assessment?.mold_or_damage_visible === "Yes" &&
                    <ViewRow label="6.0 Mold description" value={viewing.assessment?.mold_description} multiline />}
                  <ViewRow label="7. Contacted insurance" value={viewing.assessment?.contacted_insurance} />
                  {viewing.assessment?.contacted_insurance === "Yes" &&
                    <ViewRow label="7.0 Insurance details" value={viewing.assessment?.insurance_details} multiline />}
                  {viewing.assessment?.contacted_insurance === "No" &&
                    <ViewRow label="7.1 Insurance follow-up" value={viewing.assessment?.insurance_followup} multiline />}
                </>)}

                  {viewing.assessment_type === "hvac" && (<>
                  <ViewRow label="1. System type" value={viewing.assessment?.system_type} />
                  <ViewRow label="2. Completely non-functional" value={viewing.assessment?.is_working} />
                  <ViewRow label="3. Severity" value={viewing.assessment?.severity} />
                  <ViewRow label="4. Noticed at" value={viewing.assessment?.noticed_at} />
                  <ViewRow label="5. Indoor temp (°F)" value={viewing.assessment?.indoor_temp_f} />
                  <ViewRow label="6. System age (years)" value={viewing.assessment?.system_age_years} />
                  <ViewRow label="7. Last maintenance" value={viewing.assessment?.last_maintenance_date} />
                  <ViewRow label="8. Error code" value={viewing.assessment?.error_code} />
                  <ViewRow label="9. Description" value={viewing.assessment?.failure_description} multiline />
                  <ViewRow label="10. Contacted insurance" value={viewing.assessment?.contacted_insurance} />
                  {viewing.assessment?.contacted_insurance === "Yes" &&
                    <ViewRow label="10.0 Insurance details" value={viewing.assessment?.insurance_details} multiline />}
                  {viewing.assessment?.contacted_insurance === "No" &&
                    <ViewRow label="10.1 Insurance follow-up" value={viewing.assessment?.insurance_followup} multiline />}
                </>)}

                <AssessmentPhotos ids={(viewing.photo_paths && viewing.photo_paths.length ? viewing.photo_paths : (viewing.assessment?.photo_paths || []))} />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Filter tabs */}
      <div className="flex items-center gap-2 mb-4 border-b border-slate-200">
        <button
          data-testid="maint-tab-all"
          onClick={() => setFilter("all")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${filter === "all" ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"}`}
        >
          All requests <span className="ml-1 text-xs text-slate-400">({items.length})</span>
        </button>
        <button
          data-testid="maint-tab-assessments"
          onClick={() => setFilter("assessments")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${filter === "assessments" ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"}`}
        >
          Assessments log <span className="ml-1 text-xs text-slate-400">({assessmentCount})</span>
        </button>
      </div>

      {visibleItems.length === 0 ? (
        <EmptyState
          kind="maintenance"
          eyebrow={filter === "assessments" ? "Pristine condition" : "All quiet on the estate"}
          title={filter === "assessments" ? "No structured assessments yet." : "No maintenance requests yet."}
          body={filter === "assessments" ? "Open an Appliance, Plumbing or HVAC assessment to document the issue with photos and skip-logic forms." : "When tenants submit requests, they will appear here for your review."}
        />
      ) : (
        <div className="space-y-3" data-testid="maintenance-list">
          {visibleItems.map(m => (
            <Card key={m.id} data-testid={`maintenance-${m.id}`} className="p-5 border-slate-200 hover:shadow-md transition-shadow duration-200">
              <div className="flex items-start gap-4">
                <div className="h-10 w-10 rounded-md bg-slate-100 flex items-center justify-center shrink-0">
                  <AlertCircle className="h-5 w-5 text-slate-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h4 className="font-display font-semibold">{m.title}</h4>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${PRIORITY_COLORS[m.priority]}`}>{m.priority}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${STATUS_COLORS[m.status]}`}>{m.status.replace("_", " ")}</span>
                    {m.assessment_type && (
                      <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full font-semibold bg-slate-900 text-white">{m.assessment_type}</span>
                    )}
                    {((m.photo_paths && m.photo_paths.length) || (m.assessment?.photo_paths?.length)) > 0 && (
                      <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">
                        <Paperclip className="h-3 w-3" /> {(m.photo_paths?.length || m.assessment?.photo_paths?.length || 0)}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-600 mb-2">{m.description}</p>
                  <div className="text-xs text-slate-500">
                    {m.property_name} · {m.requester_name} · {(m.created_at || "").slice(0, 10)}
                    {m.assessment_type && (
                      <button onClick={() => setViewing(m)} data-testid={`view-assessment-${m.id}`} className="ml-2 inline-flex items-center gap-1 text-slate-700 hover:text-slate-900 font-medium">
                        <Eye className="h-3 w-3" /> View {m.assessment_type} assessment
                      </button>
                    )}
                  </div>
                </div>
                {user?.role !== "tenant" && (
                  <div className="flex items-center gap-2">
                    {m.assessment_type && (
                      <AssessmentExportMenu
                        maintenanceId={m.id}
                        filenameBase={`rojasproperty-${m.assessment_type}`}
                      />
                    )}
                    {isAdmin && (
                      <Select value={m.status} onValueChange={(v) => updateStatus(m.id, v)}>
                        <SelectTrigger data-testid={`maint-status-${m.id}`} className="w-40 h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="open">Open</SelectItem><SelectItem value="in_progress">In Progress</SelectItem><SelectItem value="resolved">Resolved</SelectItem><SelectItem value="closed">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                    {isAdmin && (
                      <>
                        <Button data-testid={`edit-maint-${m.id}`} variant="ghost" size="icon" className="h-9 w-9" onClick={() => openEdit(m)}>
                          <Pencil className="h-4 w-4 text-slate-500" />
                        </Button>
                        <Button data-testid={`delete-maint-${m.id}`} variant="ghost" size="icon" className="h-9 w-9" onClick={() => setConfirmDelete(m)}>
                          <Trash2 className="h-4 w-4 text-rose-500" />
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) { setEditing(null); setEditForm(null); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="maint-edit-dialog">
          {editing && editForm && (
            <>
              <DialogHeader>
                <DialogTitle>Edit {editing.assessment_type ? `${editing.assessment_type} assessment` : "maintenance request"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 pt-2">
                <div className="space-y-1.5">
                  <Label className="text-xs">Title</Label>
                  <Input data-testid="edit-maint-title" value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Description</Label>
                  <Textarea data-testid="edit-maint-description" rows={3} value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Landlord notes</Label>
                  <Textarea data-testid="edit-maint-notes" rows={2} value={editForm.landlord_notes} onChange={(e) => setEditForm({ ...editForm, landlord_notes: e.target.value })} />
                </div>
                {editing.assessment_type && Object.keys(editForm.assessment || {}).length > 0 && (
                  <div className="pt-2 border-t border-bone">
                    <div className="micro-label text-gold mb-2">Assessment fields</div>
                    <div className="space-y-2 max-h-64 overflow-y-auto pr-2">
                      {Object.entries(editForm.assessment).map(([k, v]) => (
                        <div key={k} className="grid grid-cols-3 gap-2 items-start">
                          <Label className="text-xs text-slate-600 pt-2 truncate">{k.replace(/_/g, " ")}</Label>
                          {String(v || "").length > 60 ? (
                            <Textarea rows={2} className="col-span-2" value={v || ""} onChange={(e) => setEditForm({ ...editForm, assessment: { ...editForm.assessment, [k]: e.target.value } })} />
                          ) : (
                            <Input className="col-span-2" value={v || ""} onChange={(e) => setEditForm({ ...editForm, assessment: { ...editForm.assessment, [k]: e.target.value } })} />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setEditing(null); setEditForm(null); }}>Cancel</Button>
                <Button data-testid="save-maint-edit" onClick={saveEdit} className="rounded-none bg-ink text-ivory hover:bg-gold hover:text-ink uppercase tracking-[0.2em] text-xs font-semibold gap-1.5">
                  <Save className="h-3.5 w-3.5" /> Save changes
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="delete-maint-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this maintenance record?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete && <>This will permanently remove <strong>{confirmDelete.title}</strong> and any attached photos / assessment data. This action cannot be undone.</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-maint" onClick={removeMaintenance} className="bg-rose-600 hover:bg-rose-700">Yes, delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}

function AssessmentPhotos({ ids }) {
  const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
  if (!ids || !ids.length) return null;
  return (
    <div className="pt-3 border-t border-slate-200">
      <div className="flex items-center gap-1.5 text-xs text-slate-500 font-medium mb-2">
        <ImageIcon className="h-3.5 w-3.5" /> Attached photos ({ids.length})
      </div>
      <div className="grid grid-cols-3 gap-2" data-testid="assessment-photos">
        {ids.map((id) => (
          <a
            key={id}
            href={`${BACKEND}/api/files/${id}/download`}
            target="_blank"
            rel="noreferrer"
            className="block border border-slate-200 rounded-md overflow-hidden bg-slate-50 hover:border-slate-400 transition-colors"
            data-testid={`assessment-photo-${id}`}
          >
            <img
              src={`${BACKEND}/api/files/${id}/download`}
              alt="attachment"
              loading="lazy"
              className="w-full h-28 object-cover"
              onError={(e) => { e.target.style.display = "none"; e.target.parentElement.classList.add("p-3", "text-xs", "text-slate-500"); e.target.parentElement.innerText = `Photo ${id.slice(0, 8)}`; }}
            />
          </a>
        ))}
      </div>
    </div>
  );
}

function ViewRow({ label, value, multiline }) {
  return (
    <div className={`flex ${multiline ? "flex-col gap-1" : "items-start gap-3"} border-b border-slate-100 pb-2`}>
      <div className="text-xs text-slate-500 font-medium min-w-[180px]">{label}</div>
      <div className={`text-slate-800 ${multiline ? "whitespace-pre-wrap" : ""}`}>{value || <span className="text-slate-300">—</span>}</div>
    </div>
  );
}
